/**
 * PIScoreSummary Component
 * Displays the running score summary in the right panel of the PI Calculator
 * Gradient card design with score breakdown
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { XCircle, RotateCcw, Printer, BookOpen } from 'lucide-react';
import type { PICalculatedScore } from '@/lib/pi-calculator-utils';

interface PIScoreSummaryProps {
  score: PICalculatedScore;
  piWeight: number; // From practice weights (typically 25)
  onReset?: () => void; // Callback to reset all PI calculator inputs
  /** When true (e.g. locked year), mutating quick actions are disabled. */
  disabled?: boolean;
}

/**
 * Score Breakdown Row Component
 * Layout: [Label (fixed width)] [Progress Bar (flex)] [Score Text (fixed width)]
 * Simple blue bar design for consistency
 */
function BreakdownRow({ 
  label, 
  points, 
  maxPoints, 
  isLast = false,
}: { 
  label: string; 
  points: number; 
  maxPoints: number;
  isLast?: boolean;
}) {
  const percent = maxPoints > 0 ? (points / maxPoints) * 100 : 0;

  return (
    <div 
      className={`flex items-center gap-3 py-3 ${!isLast ? 'border-b' : ''}`}
      style={{ borderColor: '#F0F2F5' }}
    >
      {/* Label - fixed width */}
      <div className="text-sm font-medium text-gray-700 w-36 flex-shrink-0">{label}</div>
      
      {/* Progress bar - flex grow */}
      <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all duration-200 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
      
      {/* Score text - fixed width, right aligned */}
      <div className="text-sm font-medium w-14 text-right flex-shrink-0 text-gray-700">
        {Number.isInteger(points) ? points : points.toFixed(1)} | {maxPoints}
      </div>
    </div>
  );
}

export function PIScoreSummary({ score, piWeight, onReset, disabled = false }: PIScoreSummaryProps) {
  // Ensure piWeight defaults to 25 if not provided (but respects 0 for reweighting)
  const effectivePiWeight = piWeight ?? 25;
  const mipsContribution = (score.totalScore * effectivePiWeight) / 100;
  
  // Combine e-Prescribing and Query of PDMP into single e-Prescribing category
  const ePrescribingTotal = score.breakdown.ePrescribing.points + score.breakdown.queryPdmp.points;
  const ePrescribingMax = score.breakdown.ePrescribing.maxPoints + score.breakdown.queryPdmp.maxPoints;
  
  const prerequisitesNotMet = !score.prerequisitesPassed;
  

  return (
    <div className="space-y-4 sticky top-4">
      {/* Total PI Score Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-6">
              {/* Circular progress indicator - Larger */}
              <div className="relative w-32 h-32 flex-shrink-0">
                <svg className="w-32 h-32 -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="#E5E7EB"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    className="stroke-primary"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${(score.totalScore / 100) * 264} 264`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-gray-900 leading-none">{score.totalScore.toFixed(0)}</span>
                  <span className="text-xs text-gray-400 mt-0.5">/ 100</span>
                </div>
              </div>
              {/* Text content */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Total Promoting Interoperability (PI) Score</h3>
                <p className="text-sm text-gray-500 mt-1">Based on current selections</p>
              </div>
            </div>
          </div>
          {/* Dark blue footer section */}
          <div className="bg-primary px-6 py-4">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-white/80 uppercase tracking-wide">Weighted Category Score</span>
              <span className="text-base font-bold text-white">{mipsContribution.toFixed(2)} pts</span>
            </div>
          </div>
        </div>

      {/* Prerequisite Status - Only show when failed */}
      {prerequisitesNotMet && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">Prerequisites Not Met</span>
            </div>
            <p className="text-sm text-destructive/80 mt-1">
              All four prerequisite attestations must be "Yes" before any PI points can be earned.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Score Breakdown Card */}
      <Card>
        <CardContent className="p-0">
          {/* Header with divider */}
          <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
            <h3 className="text-base font-semibold text-gray-900">Score Breakdown</h3>
          </div>
          
          {/* Breakdown rows */}
          <div className="px-5 py-1">
            <BreakdownRow 
              label="e-Prescribing" 
              points={ePrescribingTotal} 
              maxPoints={ePrescribingMax}
            />
            <BreakdownRow 
              label="Patient Access" 
              points={score.breakdown.patientAccess.points} 
              maxPoints={score.breakdown.patientAccess.maxPoints}
            />
            <BreakdownRow 
              label="Health Info Exchange" 
              points={score.breakdown.hie.points} 
              maxPoints={score.breakdown.hie.maxPoints}
            />
            <BreakdownRow 
              label="Public Health" 
              points={score.breakdown.publicHealth.points} 
              maxPoints={score.breakdown.publicHealth.maxPoints}
              isLast={score.breakdown.bonus.points === 0}
            />
            
            {/* Bonus Points - Only show when earned */}
            {score.breakdown.bonus.points > 0 && (
              <div className="flex items-center justify-between pt-3 mt-1 border-t" style={{ borderColor: '#F0F2F5' }}>
                <span className="text-sm text-emerald-600 font-medium">Bonus Points</span>
                <span className="text-sm font-bold text-emerald-600">+{score.breakdown.bonus.points}</span>
              </div>
            )}
          </div>
          
          {/* Footer hint */}
          <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-xs text-gray-500">
              Shows points earned for each PI objective.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions Card */}
      <Card>
        <CardContent className="p-0">
          {/* Header with divider */}
          <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
            <h3 className="text-base font-semibold text-gray-900">Quick Actions</h3>
          </div>
          
          {/* Action rows */}
          <div className="px-3 py-2">
            <button
              onClick={onReset}
              disabled={disabled}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Reset All</span>
            </button>
            
            <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
            
            <button
              onClick={() => window.print()}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Print Summary</span>
            </button>
            
            <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
            
            <button
              onClick={() => window.open('https://qpp-cm-prod-content.s3.amazonaws.com/uploads/3112/2025-Promoting-Interoperability-Quick-Start-Guide.pdf', '_blank', 'noopener,noreferrer')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">PI Quick Start Guide</span>
            </button>
          </div>
          
          {/* Footer hint */}
          <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
            <p className="text-xs text-gray-500">
              These tools apply to your PI Calculator inputs only.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
